import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ChevronDown, LogOut, User } from 'lucide-react'
import { useSessionStore } from '@/stores/sessionStore'
import { useLogout } from '@/api/auth'

function capitalize(s: string): string {
  const t = s.trim()
  if (!t) return ''
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()
}

// Render the BE-provided username as "First L." when we can recover a
// first/last split, otherwise as a single capitalized token. The auth
// response only carries `username` (sometimes a login like
// "jessesmith", sometimes "jesse smith", sometimes an email), so we
// probe the common separators in priority order.
function formatAuthUsername(raw: string | null | undefined): string {
  if (!raw) return ''
  const trimmed = raw.trim()
  if (!trimmed) return ''

  // Email — split the local part the same way as a plain username.
  const local = trimmed.includes('@') ? trimmed.split('@')[0] : trimmed
  const parts = local.split(/[\s._-]+/).filter(Boolean)

  if (parts.length >= 2) {
    return `${capitalize(parts[0])} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`
  }
  return capitalize(parts[0] ?? trimmed)
}

export function AuthUserBadge() {
  const navigate = useNavigate()
  const authType = useSessionStore((s) => s.authType)
  const authUsername = useSessionStore((s) => s.authUsername)
  const logoutMutation = useLogout()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  if (authType !== 'manager' || !authUsername) return null

  const display = formatAuthUsername(authUsername)

  const handleLogout = () => {
    setOpen(false)
    // useLogout('full') clears the BE clerk session, the persisted
    // session state, AND the react-query cache. clearSession() alone
    // skipped the query cache, so stale auth/customer/order data
    // would shadow the next user's session until a hard reload.
    logoutMutation.mutate('full', {
      onSettled: () => navigate({ to: '/auth' }),
    })
  }

  return (
    <div ref={containerRef} className="ml-auto relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={authUsername}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
      >
        <User className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-700 truncate max-w-[180px] sm:max-w-[260px]">
          {display}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            <LogOut className="w-4 h-4 text-gray-500" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  )
}
